import { tokenizeContent } from "@/lib/content/tokenizer";
import {
  UserBaseAggregatedCustomTestPerformanceSchema,
  UserBaseAggregatedMockExamPerformanceSchema,
  UserBaseAggregatedPerformanceSchema,
  UserBaseAggregatedTopicTestPerformanceSchema,
} from "@/types/user-metadata-schema";
import type {
  BlogArticleFull,
  BlogArticleListItem,
  BlogSitemapEntry,
} from "@tsTypes/blog-schema";
import {
  RawCramSheetEntry,
  TopicCramSheets,
} from "@tsTypes/cramsheet-schema";
import { Definition } from "@tsTypes/definition-schema";
import { Flashcard, SubTopicFlashcards } from "@tsTypes/flash-schema";
import {
  MainMetaMap,
  MainMetaMapSubject,
  MainMetaMapSubTopic,
  MainMetaMapTopic,
  RawMainMetaMapSubjectResponse,
} from "@tsTypes/meta-schema";
import { Note, SubTopicNotes } from "@tsTypes/note-schema";
import { Question, SubTopicQuestions } from "@tsTypes/question-schema";
import { School } from "@tsTypes/school-schema";
import {
  AverageSubjectGradeBoundariesSchema,
  Subject,
  SubTopic,
  Topic,
} from "@tsTypes/subject-schema";
import { RawStrapiVideoResponse, TopicVideos } from "@tsTypes/video-schema";
import parse from "html-react-parser";
import {
  renderCramSheets,
  renderDefinitionArray,
  renderFlashCardArray,
  renderNotesForSubTopics,
  renderQuestionArray,
  renderVideos,
} from "../katex/render";
import { strapiData } from "../strapi/strapiApi";
import {
  blogArticleCacheTags,
  blogIndexCacheTags,
  ContentCacheContext,
  contentCacheTags,
  metaCacheTags,
} from "./cacheTags";
import { arrangeQuestions, buildKeymap } from "./formatters";
import { getSubTopicDocumentIdsForTopic } from "./helpers";
import { convertQuestionsByTopicToQuestionTotalsBySubTopic } from "./testBuilder";
const strapiKey = process.env.STRAPI_API_TOKEN!;
// **************************************************************************************** FETCHING
export async function getUserBaseAggregatedTestPeformance() {
  const refetchInterval = 604800; // refetch outside cache every 7 days to capture latest CRON
  const topicTestsPromise: Promise<UserBaseAggregatedTopicTestPerformanceSchema> =
    strapiData(
      strapiKey,
      "admin-actions/topic-tests-performance",
      undefined,
      undefined,
      undefined,
      refetchInterval,
    );
  const mockExamsPromise: Promise<UserBaseAggregatedMockExamPerformanceSchema> =
    strapiData(
      strapiKey,
      "admin-actions/mock-exams-performance",
      undefined,
      undefined,
      undefined,
      refetchInterval,
    );
  const customTestsPromise: Promise<UserBaseAggregatedCustomTestPerformanceSchema> =
    strapiData(
      strapiKey,
      "admin-actions/custom-tests-performance",
      undefined,
      undefined,
      undefined,
      refetchInterval,
    );
  const [topicTests, mockExams, customTests] = await Promise.all([
    topicTestsPromise,
    mockExamsPromise,
    customTestsPromise,
  ]);
  return {
    topicTests,
    mockExams,
    customTests,
  };
}

export async function getUserBaseAggregatedPerformance() {
  const data: UserBaseAggregatedPerformanceSchema = await strapiData(
    strapiKey,
    "admin-actions/subject-performance",
    undefined,
    undefined,
    undefined,
    604800, // refetch outside cache every 7 days to capture latest CRON
  );
  return data;
}

export async function getSubjectAverageGradeBoundaries() {
  const data: AverageSubjectGradeBoundariesSchema = await strapiData(
    strapiKey,
    "admin-actions/grade-boundaries",
  );
  return data;
}

export async function getSubjects(
  withSyllabusPoints?: true,
  withDraftSubjects?: true,
  withTopicTests?: true,
) {
  const data: Subject[] = await strapiData(
    strapiKey,
    "subjects",
    buildSubjectsQuery(withSyllabusPoints, withDraftSubjects, withTopicTests),
    undefined,
    undefined,
    undefined,
    undefined,
    metaCacheTags(),
  );
  return data;
}

export async function getTopicSyllabusPointData(
  topicDocumentId: string,
  ctx?: ContentCacheContext,
) {
  const tags = [
    ...metaCacheTags(),
    ...contentCacheTags({
      subjectDocumentId: ctx?.subjectDocumentId,
      topicDocumentId,
    }),
  ];
  const data: {
    documentId: Topic["documentId"];
    TopicDescription: Topic["TopicDescription"];
    RelatedSubTopics: Pick<SubTopic, "documentId" | "RelatedSyllabusPoints">[];
  } = await strapiData(
    strapiKey,
    "topics",
    buildTopicSyllabusPointDataQuery(),
    topicDocumentId,
    undefined,
    undefined,
    undefined,
    tags,
  );
  data.RelatedSubTopics.forEach((st) => {
    st.RelatedSyllabusPoints.forEach((p) => {
      p.Point = tokenizeContent(p.Point as unknown as string, "syllabus");
    });
  });
  return data;
}

export async function getMainMetaMap(fetchDrafts?: true) {
  const data: RawMainMetaMapSubjectResponse[] = await strapiData(
    strapiKey,
    "subjects",
    buildMainMetaMapQuery(fetchDrafts),
    undefined,
    undefined,
    undefined,
    undefined,
    metaCacheTags(),
  );
  return data.reduce((acc: MainMetaMap, subject) => {
    if (!acc[subject.documentId]) {
      acc[subject.documentId] = {
        title: subject.Name,
      };
    }
    subject.RelatedTopics.forEach((topic) => {
      if (!acc[topic.documentId]) {
        (acc[topic.documentId] as MainMetaMapTopic) = {
          index: topic.TopicNumber,
          number: topic.TopicNumber.toString(),
          title: topic.TopicTitle,
          isHL: topic.IsHigherLevel,
          subjectDocumentId: subject.documentId,
        };
      }
      topic.RelatedSubTopics.forEach((subTopic) => {
        if (!acc[subTopic.documentId]) {
          (acc[subTopic.documentId] as MainMetaMapSubTopic) = {
            index: subTopic.SubTopicNumber,
            label:
              subTopic.OfficialTag ??
              `${topic.TopicNumber}.${subTopic.SubTopicNumber}`,
            title: subTopic.SubTopicTitle,
            isHL: subTopic.IsHigherLevel,
            subThemes:
              subTopic.RelatedSubThemes.length > 0
                ? subTopic.RelatedSubThemes.reduce(
                    (acc: MainMetaMapSubTopic["subThemes"], subTheme) => {
                      acc![subTheme.documentId] = {
                        index: subTheme.SubThemeNumber,
                        title: subTheme.SubThemeTitle,
                      };
                      return acc;
                    },
                    {},
                  )
                : undefined,
            topicDocumentId: topic.documentId,
          };
        }
      });
    });
    return acc;
  }, {});
}

export async function getSubjectMetaMap(subjectDocumentId: string) {
  const mainMetaMap = await getMainMetaMap();
  return mainMetaMap[subjectDocumentId] as MainMetaMapSubject;
}

export async function getTopicMetaMap(topicDocumentId: string) {
  const mainMetaMap = await getMainMetaMap();
  return mainMetaMap[topicDocumentId] as MainMetaMapTopic;
}

export async function getSubTopicMetaMap(subTopicDocumentId: string) {
  return (await getMainMetaMap())[subTopicDocumentId] as MainMetaMapSubTopic;
}

export function findRelatedSubTopicMetaFromSubTopicDocumentId(
  subTopicDocumentId: string,
  mainMetaMap: MainMetaMap,
) {
  return mainMetaMap[subTopicDocumentId] as MainMetaMapSubTopic;
}

export function findRelatedTopicMetaFromSubTopicDocumentId(
  subTopicDocumentId: string,
  mainMetaMap: MainMetaMap,
) {
  return mainMetaMap[
    (mainMetaMap[subTopicDocumentId] as MainMetaMapSubTopic).topicDocumentId
  ] as MainMetaMapTopic;
}

export function findRelatedSubjectMetaFromTopicDocumentId(
  topicDocumentId: string,
  mainMetaMap: MainMetaMap,
) {
  return mainMetaMap[
    (mainMetaMap[topicDocumentId] as MainMetaMapTopic).subjectDocumentId
  ] as MainMetaMapSubject;
}

export function findRelatedSubjectMetaFromSubTopicDocumentId(
  subTopicDocumentId: string,
  mainMetaMap: MainMetaMap,
) {
  return mainMetaMap[
    (
      mainMetaMap[
        (mainMetaMap[subTopicDocumentId] as MainMetaMapSubTopic).topicDocumentId
      ] as MainMetaMapTopic
    ).subjectDocumentId
  ] as MainMetaMapSubject;
}

export function findRelatedTopicMetaFromSubjectDocumentId(
  subjectDocumentId: string,
  mainMetaMap: MainMetaMap,
) {
  return Object.fromEntries(
    Object.entries(mainMetaMap).filter(
      ([key, meta]) =>
        "subjectDocumentId" in meta &&
        meta.subjectDocumentId === subjectDocumentId,
    ) as [string, MainMetaMapTopic][],
  );
}

export function findRelatedSubTopicMetaFromTopicDocumentId(
  topicDocumentId: string,
  mainMetaMap: MainMetaMap,
) {
  return Object.fromEntries(
    Object.entries(mainMetaMap).filter(
      ([key, meta]) =>
        "topicDocumentId" in meta && meta.topicDocumentId === topicDocumentId,
    ) as [string, MainMetaMapSubTopic][],
  );
}

export function getSlHlSplitSubTopicDocumentIdsFromSubjectDocumentId(
  subjectDocumentId: string,
  mainMetaMap: MainMetaMap,
) {
  const relevantSubTopicDocumentIds: { sl: string[]; hl: string[] } = {
    sl: [],
    hl: [],
  };
  // first find topic Ids for the subject
  const relevantTopicDocumentIds = Object.entries(mainMetaMap).map(
    ([key, meta]) =>
      "subjectDocumentId" in meta &&
      meta.subjectDocumentId === subjectDocumentId
        ? key
        : undefined,
  );
  Object.entries(mainMetaMap).forEach(([key, meta]) => {
    if (
      "topicDocumentId" in meta &&
      relevantTopicDocumentIds.includes(meta.topicDocumentId)
    ) {
      if (meta.isHL) {
        relevantSubTopicDocumentIds.hl.push(key);
      } else {
        relevantSubTopicDocumentIds.sl.push(key);
      }
    }
  });
  return relevantSubTopicDocumentIds;
}

export async function getSchools() {
  const data: School[] = await strapiData(
    strapiKey,
    "schools",
    "fields[0]=Name",
  );
  return data;
}

export async function fetchAllBlogArticles(): Promise<BlogArticleListItem[]> {
  return strapiData(
    strapiKey,
    "blog-articles",
    buildBlogArticlesListQuery(),
    undefined,
    undefined,
    undefined,
    undefined,
    blogIndexCacheTags(),
  ) as Promise<BlogArticleListItem[]>;
}

export async function fetchBlogArticleBySlug(
  slug: string,
): Promise<BlogArticleFull | null> {
  const rows = (await strapiData(
    strapiKey,
    "blog-articles",
    buildBlogArticleBySlugQuery(slug),
    undefined,
    undefined,
    undefined,
    1,
    blogArticleCacheTags(slug),
  )) as BlogArticleFull[];
  return rows[0] ?? null;
}

export async function fetchAllBlogSlugsForSitemap(): Promise<
  BlogSitemapEntry[]
> {
  type BlogSitemapRow = {
    slug: string;
    updatedAt: string;
    publishedAt: string;
  };
  const rows = (await strapiData(
    strapiKey,
    "blog-articles",
    buildBlogSitemapSlugsQuery(),
    undefined,
    undefined,
    undefined,
    undefined,
    blogIndexCacheTags(),
  )) as BlogSitemapRow[];
  return rows.map((row) => ({
    slug: row.slug,
    lastmod: row.updatedAt,
  }));
}

// returns rendered and organised array with MC first
export async function getQuestions(
  currentSubTopics: string[],
  ctx?: ContentCacheContext,
) {
  const ques: Question[] = await strapiData(
    strapiKey,
    "questions",
    buildQuestionsQuery(currentSubTopics),
    undefined,
    undefined,
    undefined,
    undefined,
    contentCacheTags({
      subjectDocumentId: ctx?.subjectDocumentId,
      topicDocumentId: ctx?.topicDocumentId,
      subtopicDocumentIds: currentSubTopics,
      resource: "questions",
    }),
  );
  let addressedQuestions: string[] = [];
  // restructure questions by subtopic
  const organised = currentSubTopics.reduce(
    (acc: { [subTopicId: string]: Question[] }, documentId: string) => {
      const relevantQuestions = ques.filter((q) =>
        q.RelatedSubTopics.some(
          (s) =>
            s.documentId === documentId &&
            !addressedQuestions.includes(q.documentId),
        ),
      );
      addressedQuestions.push(...relevantQuestions.map((q) => q.documentId));
      if (!acc[documentId]) {
        acc[documentId] = [];
      }
      acc[documentId].push(...relevantQuestions);
      return acc;
    },
    {},
  );
  const data: SubTopicQuestions[] = Object.entries(organised).map(
    ([key, value]) => {
      const rendered = renderQuestionArray(value);
      return {
        documentId: key,
        questions: arrangeQuestions(rendered),
      } as SubTopicQuestions;
    },
  );
  return data;
}

export async function getNotes(
  currentSubTopics: string[],
  ctx?: ContentCacheContext,
): Promise<SubTopicNotes[]> {
  const [notes, definitions] = await Promise.all([
    strapiData(
      strapiKey,
      "notes",
      buildNotesQuery(currentSubTopics),
      undefined,
      undefined,
      undefined,
      undefined,
      contentCacheTags({
        subjectDocumentId: ctx?.subjectDocumentId,
        topicDocumentId: ctx?.topicDocumentId,
        subtopicDocumentIds: currentSubTopics,
        resource: "notes",
      }),
    ) as Promise<Note[]>,
    getDefinitions(currentSubTopics, undefined, ctx),
  ]);
  const keywordMap = buildKeymap(definitions);
  return renderNotesForSubTopics(notes, currentSubTopics, keywordMap);
}

export async function getFlashcards(
  currentSubTopics: string[],
  ctx?: ContentCacheContext,
) {
  const flashcards: Flashcard[] = await strapiData(
    strapiKey,
    "flashcards",
    buildFlashCardQuery(currentSubTopics),
    undefined,
    undefined,
    undefined,
    undefined,
    contentCacheTags({
      subjectDocumentId: ctx?.subjectDocumentId,
      topicDocumentId: ctx?.topicDocumentId,
      subtopicDocumentIds: currentSubTopics,
      resource: "flashcards",
    }),
  );
  const organised = flashcards.reduce(
    (acc: { [subTopicId: string]: Flashcard[] }, f) => {
      const relevantSubTopicId = f.RelatedSubTopics.find((s) =>
        currentSubTopics.includes(s.documentId),
      )!.documentId;
      if (!acc[relevantSubTopicId]) {
        acc[relevantSubTopicId] = [f];
      } else {
        acc[relevantSubTopicId] = [...acc[relevantSubTopicId], f];
      }
      return acc;
    },
    {},
  );
  const data: SubTopicFlashcards[] = Object.entries(organised).map(
    ([key, value]) => {
      return {
        documentId: key,
        flashcards: renderFlashCardArray(value),
      } as SubTopicFlashcards;
    },
  );
  return data;
}

export async function getDefinitions(
  currentSubTopics: string[],
  noCache?: true,
  ctx?: ContentCacheContext,
) {
  const data: Definition[] = await strapiData(
    strapiKey,
    "definitions",
    buildDefinitionQuery(currentSubTopics),
    undefined,
    noCache ? true : undefined,
    noCache ? undefined : 54432000, // refetch outside cache every 3 months (in case deleted/modified)
    undefined,
    noCache
      ? undefined
      : contentCacheTags({
          subjectDocumentId: ctx?.subjectDocumentId,
          topicDocumentId: ctx?.topicDocumentId,
          subtopicDocumentIds: currentSubTopics,
          resource: "definitions",
        }),
  );
  // remove any subtopics from other subjects so filtering on sl/hl works better
  data.map((d) => {
    d.RelatedSubTopics = d.RelatedSubTopics.filter((st) =>
      currentSubTopics.includes(st.documentId),
    );
  });
  // sort alphabetically
  data.sort((a, b) => {
    const termA = parse(a.Term) as string;
    const termB = parse(b.Term) as string;
    return termA.localeCompare(termB, undefined, { sensitivity: "base" });
  });
  return renderDefinitionArray(data);
}

export async function getVideos(
  subTopicDocumentIds: string[],
  ctx?: ContentCacheContext,
): Promise<TopicVideos> {
  const [videos, definitions] = await Promise.all([
    strapiData(
      strapiKey,
      "videos",
      buildVideosQuery(subTopicDocumentIds),
      undefined,
      undefined,
      undefined,
      undefined,
      contentCacheTags({
        subjectDocumentId: ctx?.subjectDocumentId,
        topicDocumentId: ctx?.topicDocumentId,
        subtopicDocumentIds: subTopicDocumentIds,
        resource: "videos",
      }),
    ) as Promise<RawStrapiVideoResponse>,
    getDefinitions(subTopicDocumentIds, undefined, ctx),
  ]);
  const keywordMap = buildKeymap(definitions);
  return renderVideos(videos, subTopicDocumentIds, keywordMap);
}

export async function getCramSheets(
  subTopicDocumentIds: string[],
  ctx?: ContentCacheContext,
): Promise<TopicCramSheets> {
  const [data, definitions] = await Promise.all([
    strapiData(
      strapiKey,
      "cram-sheets",
      buildCramSheetQuery(subTopicDocumentIds),
      undefined,
      undefined,
      undefined,
      undefined,
      contentCacheTags({
        subjectDocumentId: ctx?.subjectDocumentId,
        topicDocumentId: ctx?.topicDocumentId,
        subtopicDocumentIds: subTopicDocumentIds,
        resource: "cram-sheets",
      }),
    ) as Promise<RawCramSheetEntry[]>,
    getDefinitions(subTopicDocumentIds, undefined, ctx),
  ]);
  const keywordMap = buildKeymap(definitions);
  return renderCramSheets(data, subTopicDocumentIds, keywordMap);
}

// **************************************************************************************** QUERIES
export function buildGradeBoundariesQuery() {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: "published",
      fields: ["Level", "ExamPeriod", "Grades"],
      populate: {
        RelatedSubject: {
          fields: ["Name"],
        },
      },
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export function buildSubjectsQuery(
  withSyllabusPoints?: true,
  withDraftSubjects?: true,
  withTopicTests?: true,
) {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: withDraftSubjects ? "draft" : "published",
      fields: ["Name"],
      populate: {
        RelatedTopics: {
          fields: [
            "TopicNumber",
            "TopicTitle",
            "IsHigherLevel",
            "TopicDescription",
          ],
          sort: ["TopicNumber"],
          populate: {
            RelatedSubTopics: {
              fields: [
                "SubTopicNumber",
                "SubTopicTitle",
                "SubTopicDescription",
                "IsHigherLevel",
                "OfficialTag",
              ],
              sort: ["OfficialTag", "IsHigherLevel", "SubTopicNumber"],
              populate: {
                RelatedSyllabusPoints: withSyllabusPoints
                  ? {
                      fields: ["Point", "TypeOfPoint", "SyllabusPointNumber"],
                    }
                  : undefined,
                RelatedSubThemes: {
                  fields: [
                    "SubThemeNumber",
                    "SubThemeTitle",
                    "SubThemeDescription",
                  ],
                  sort: ["SubThemeNumber"],
                },
              },
            },
            RelatedTopicTests: withTopicTests
              ? {
                  fields: ["QuestionSource", "IsHigherLevel"],
                }
              : undefined,
          },
        },
      },
      sort: ["Name"],
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

function buildTopicSyllabusPointDataQuery() {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: "published",
      fields: ["documentId", "TopicDescription"],
      populate: {
        RelatedSubTopics: {
          fields: ["documentId", "SubTopicNumber"],
          populate: {
            RelatedSyllabusPoints: {
              fields: [
                "documentId",
                "Point",
                "TypeOfPoint",
                "SyllabusPointNumber",
              ],
            },
          },
          sort: ["OfficialTag", "IsHigherLevel", "SubTopicNumber"],
        },
      },
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export function buildSubjectTestQuery() {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: "published",
      fields: ["Name"],
      populate: {
        RelatedTopics: {
          fields: [
            "TopicNumber",
            "TopicTitle",
            "IsHigherLevel",
            "TopicDescription",
          ],
          sort: ["TopicNumber"],
          populate: {
            RelatedTopicTests: {
              fields: ["Number"],
            },
          },
        },
      },
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export function buildDefinitionQuery(
  subTopicDocumentIds: string[],
  getDrafts?: boolean,
) {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: getDrafts ? "draft" : "published",
      fields: ["Term", "Definition"],
      populate: {
        RelatedSubTopics: {
          fields: ["IsHigherLevel"],
        },
      },
      filters: {
        RelatedSubTopics: {
          documentId: {
            $in: subTopicDocumentIds,
          },
        },
      },
      sort: ["Term"],
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export function buildQuestionsQuery(
  subTopicDocumentIds: string[],
  forceAllQuestions?: true,
  getDrafts?: boolean,
  getCreatedBy?: true,
) {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: getDrafts ? "draft" : "published",
      fields: ["Question", "UsefulTip", "Difficulty", "RequiresCalc"],
      populate: {
        QuestionStyle: {
          populate: "*",
        },
        RelatedSubTopics: {
          fields: [
            "SubTopicNumber",
            "SubTopicTitle",
            "IsHigherLevel",
            "OfficialTag",
          ],
        },
        RelatedSubThemes: {
          fields: ["SubThemeNumber", "SubThemeTitle"],
        },
        RelatedTopicTests: {
          fields: ["QuestionSource"],
        },
        createdBy: getCreatedBy
          ? { fields: ["firstname", "lastname"] }
          : undefined,
      },
      filters: {
        RelatedSubTopics: {
          documentId: {
            $in: subTopicDocumentIds,
          },
        },
        // Include questions not assigned to a topic test, or those assigned with at least one source other than "Test questions"
        $or: forceAllQuestions
          ? undefined
          : [
              { RelatedTopicTests: { $null: true } },
              {
                RelatedTopicTests: {
                  QuestionSource: { $ne: "Test questions" },
                },
              },
            ],
      },
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export function buildFlashCardQuery(
  subTopicDocumentIds: string[],
  getDrafts?: boolean,
) {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: getDrafts ? "draft" : "published",
      fields: ["Front", "Back"],
      populate: {
        RelatedSubTopics: true,
      },
      filters: {
        RelatedSubTopics: {
          documentId: {
            $in: subTopicDocumentIds,
          },
        },
      },
      sort: [
        "RelatedSubTopics.OfficialTag",
        "RelatedSubTopics.IsHigherLevel",
        "RelatedSubTopics.SubTopicNumber",
      ],
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export function buildNotesQuery(
  subTopicDocumentIds: string[],
  getDrafts?: boolean,
) {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: getDrafts ? "draft" : "published",
      populate: {
        NoteSection: {
          fields: ["customDocumentId", "Title", "Notes"],
        },
        RelatedSubTopic: {
          fields: ["SubTopicNumber", "SubTopicTitle", "IsHigherLevel"],
          sort: ["OfficialTag", "IsHigherLevel", "SubTopicNumber"],
        },
      },
      filters: {
        RelatedSubTopic: {
          documentId: {
            $in: subTopicDocumentIds,
          },
        },
      },
      sort: [
        "RelatedSubTopic.OfficialTag",
        "RelatedSubTopic.IsHigherLevel",
        "RelatedSubTopic.SubTopicNumber",
      ],
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export function buildVideosQuery(currentSubTopicDocumentIds: string[]) {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: "published",
      fields: ["VideoNumber", "VideoTitle", "VideoDescription", "VimeoID"],
      populate: {
        RelatedSubTopics: {
          fields: [
            "SubTopicNumber",
            "SubTopicTitle",
            "IsHigherLevel",
            "OfficialTag",
          ],
          sort: ["OfficialTag", "IsHigherLevel", "SubTopicNumber"],
        },
      },
      filters: {
        RelatedSubTopics: {
          documentId: {
            $in: currentSubTopicDocumentIds,
          },
        },
      },
      sort: [
        "RelatedSubTopics.SubTopicNumber",
        "RelatedSubTopics.IsHigherLevel",
        "VideoNumber",
      ],
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export function buildCramSheetQuery(subTopicIds: string[]) {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: "published",
      populate: {
        CramSheets: {
          fields: ["alternativeText", "url", "hash", "ext"],
        },
        RelatedSubTopics: {
          fields: [
            "SubTopicNumber",
            "SubTopicTitle",
            "IsHigherLevel",
            "OfficialTag",
            "SubTopicDescription",
          ],
          sort: ["OfficialTag", "IsHigherLevel", "SubTopicNumber"],
        },
      },
      filters: {
        RelatedSubTopics: {
          documentId: {
            $in: subTopicIds,
          },
        },
      },
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export function buildExamQuery(subjectDocumentId: string) {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: "published",
      fields: [
        "PaperNumber",
        "PaperContent",
        "PaperType",
        "IsCalculatorAllowed",
        "IsDataBookletAllowed",
      ],
      populate: {
        ExamDetails: {
          populate: ["SL", "HL"],
        },
        RelatedSubject: {
          fields: ["Name"],
        },
      },
      filters: {
        RelatedSubject: {
          documentId: {
            $eq: subjectDocumentId,
          },
        },
      },
      sort: ["PaperNumber"],
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export function buildTestQuery(topicDocumentId: string) {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: "published",
      fields: ["QuestionSource", "Number", "IsHigherLevel"],
      populate: {
        RelatedTopic: {
          populate: {
            RelatedSubTopics: {
              fields: [
                "documentId",
                "SubTopicNumber",
                "SubTopicTitle",
                "IsHigherLevel",
                "OfficialTag",
              ],
              populate: {
                RelatedTopic: {
                  fields: ["TopicNumber", "IsHigherLevel"],
                },
              },
              sort: ["OfficialTag", "IsHigherLevel", "SubTopicNumber"],
            },
            RelatedSubject: {
              fields: ["Name"],
            },
          },
        },
        RelatedQuestions2: {
          fields: ["Question", "UsefulTip", "Difficulty"],
          populate: {
            QuestionStyle: {
              populate: "*",
            },
            RelatedSubTopics: true,
          },
        },
      },
      filters: {
        RelatedTopic: {
          documentId: {
            $eq: topicDocumentId,
          },
        },
      },
      sort: ["Number", "QuestionSource"],
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export function buildMainMetaMapQuery(fetchDrafts?: true) {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: fetchDrafts ? "draft" : "published",
      fields: ["Name"],
      populate: {
        RelatedTopics: {
          fields: ["TopicNumber", "TopicTitle", "IsHigherLevel"],
          sort: ["TopicNumber"],
          populate: {
            RelatedSubTopics: {
              fields: [
                "SubTopicNumber",
                "SubTopicTitle",
                "IsHigherLevel",
                "OfficialTag",
              ],
              sort: ["OfficialTag", "IsHigherLevel", "SubTopicNumber"],
              populate: {
                RelatedSubThemes: {
                  fields: ["SubThemeNumber", "SubThemeTitle"],
                  sort: ["SubThemeNumber"],
                },
              },
            },
          },
        },
      },
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export function buildBlogArticlesListQuery() {
  const qs = require("qs");
  return qs.stringify(
    {
      status: "published",
      fields: [
        "documentId",
        "title",
        "slug",
        "metaDescription",
        "heroImageUrl",
        "body",
        "createdAt",
      ],
      sort: ["createdAt:desc"],
    },
    { encodeValuesOnly: true },
  );
}

export function buildBlogArticleBySlugQuery(slug: string) {
  const qs = require("qs");
  return qs.stringify(
    {
      status: "published",
      filters: { slug: { $eq: slug } },
      fields: [
        "documentId",
        "title",
        "slug",
        "metaDescription",
        "heroImageUrl",
        "body",
        "structuredData",
        "faqStructuredData",
        "keywords",
        "createdAt",
      ],
    },
    { encodeValuesOnly: true },
  );
}

export function buildBlogSitemapSlugsQuery() {
  const qs = require("qs");
  return qs.stringify(
    {
      status: "published",
      fields: ["slug", "updatedAt", "publishedAt"],
      sort: ["slug:asc"],
    },
    { encodeValuesOnly: true },
  );
}

function buildSchoolAggregateProgressMetasQuery() {
  const qs = require("qs");
  const query = qs.stringify(
    {
      status: "published",
      fields: ["AggregateProgressMeta"],
      filters: { SchoolId: { $eq: "all" } },
    },
    {
      encodeValuesOnly: true,
    },
  );
  return query;
}

export async function getAdminToolResourceCountsBySubTopic(
  topicDocumentIds: string[],
  mainMetaMap: MainMetaMap,
  resource: "notes" | "flashcards" | "definitions",
): Promise<Record<string, number>> {
  const qs = require("qs");
  const isSingularRelation = resource === "notes";
  const relationKey = isSingularRelation
    ? "RelatedSubTopic"
    : "RelatedSubTopics";

  const perTopicResults = await Promise.all(
    topicDocumentIds.map(async (topicDocumentId) => {
      const subTopicDocumentIds = getSubTopicDocumentIdsForTopic(
        mainMetaMap,
        topicDocumentId,
      );
      const query = qs.stringify(
        {
          fields: ["documentId"],
          populate: {
            [relationKey]: { fields: ["documentId"] },
          },
          filters: {
            [relationKey]: {
              documentId: { $in: subTopicDocumentIds },
            },
          },
        },
        { encodeValuesOnly: true },
      );

      const items = (await strapiData(
        process.env.STRAPI_API_TOKEN!,
        resource,
        query,
        undefined,
        undefined,
        300, // 5 minutes
      )) as any[];

      return { subTopicDocumentIds, items };
    }),
  );

  const result: Record<string, number> = {};
  const countedItemSubtopicPairs = new Set<string>();
  for (const { subTopicDocumentIds, items } of perTopicResults) {
    const currentTopicSubtopicSet = new Set(subTopicDocumentIds);
    for (const id of subTopicDocumentIds) {
      if (!(id in result)) result[id] = 0;
    }
    for (const item of items) {
      if (isSingularRelation) {
        const id = item[relationKey]?.documentId;
        if (!id || !(id in result) || !currentTopicSubtopicSet.has(id))
          continue;

        const pairKey = `${item.documentId}::${id}`;
        if (countedItemSubtopicPairs.has(pairKey)) continue;

        countedItemSubtopicPairs.add(pairKey);
        result[id]++;
      } else {
        const relatedSubtopics = Array.isArray(item[relationKey])
          ? item[relationKey]
          : [];
        for (const st of relatedSubtopics) {
          const subtopicId = st?.documentId;
          if (
            !subtopicId ||
            !(subtopicId in result) ||
            !currentTopicSubtopicSet.has(subtopicId)
          ) {
            continue;
          }

          const pairKey = `${item.documentId}::${subtopicId}`;
          if (countedItemSubtopicPairs.has(pairKey)) continue;

          countedItemSubtopicPairs.add(pairKey);
          result[subtopicId]++;
        }
      }
    }
  }
  return result;
}

export async function getAdminToolTestTotalsByTopic(
  topicDocumentIds: string[],
): Promise<Record<string, number>> {
  const qs = require("qs");
  const counts = await Promise.all(
    topicDocumentIds.map(async (topicDocumentId) => {
      const query = qs.stringify(
        {
          fields: ["documentId"],
          filters: {
            RelatedTopic: {
              documentId: { $eq: topicDocumentId },
            },
          },
        },
        { encodeValuesOnly: true },
      );

      const topicTests = (await strapiData(
        process.env.STRAPI_API_TOKEN!,
        "topic-tests",
        query,
        undefined,
        undefined,
        300, // 5 minutes
      )) as { documentId: string }[];

      return { topicDocumentId, count: topicTests.length };
    }),
  );

  return counts.reduce(
    (acc, { topicDocumentId, count }) => {
      acc[topicDocumentId] = count;
      return acc;
    },
    {} as Record<string, number>,
  );
}

export async function getAdminToolQuestionTotalsBySubTopic(
  topicDocumentIds: string[],
  mainMetaMap: MainMetaMap,
) {
  const qs = require("qs");
  const subjectQuestionsByTopic = await Promise.all(
    topicDocumentIds.map(async (topicDocumentId) => {
      const subTopicDocumentIds = getSubTopicDocumentIdsForTopic(
        mainMetaMap,
        topicDocumentId,
      );
      const query = qs.stringify(
        {
          fields: ["Difficulty"],
          populate: {
            QuestionStyle: {
              populate: "*", // TODO: limit to just the fields we need
            },
            RelatedSubTopics: {
              fields: ["documentId"],
            },
          },
          filters: {
            RelatedSubTopics: {
              documentId: { $in: subTopicDocumentIds },
            },
          },
        },
        { encodeValuesOnly: true },
      );

      const topicQuestions = (await strapiData(
        process.env.STRAPI_API_TOKEN!,
        "questions",
        query,
        undefined,
        undefined,
        300, // 5 minutes
      )) as Question[];
      return {
        topicDocumentId,
        subTopicDocumentIds,
        topicQuestions,
      };
    }),
  );

  return convertQuestionsByTopicToQuestionTotalsBySubTopic(
    subjectQuestionsByTopic,
  );
}
